import { createApi } from '@reduxjs/toolkit/query/react';

import baseQuery from 'src/utils/baseQuery';

export interface ChannelListType {
  channelName: string;
  channelType: string;
  configId: string;
  createdAt: string;
  id: string;
  teamId: string | null;
  tenantId: string;
  updatedAt: string;
}

export interface ConversationStatusCounts {
  open: number;
  closed: number;
  total: number;
}

// One message that matched the inbox search (POST api/messages/search)
export interface MessageSearchHit {
  messageId: string;
  conversationId: string;
  // HTML-escaped by the backend, with only <mark> around the matches
  snippet: string;
  createdAt: string;
  messageType: string;
  isPrivateNote: boolean;
  sender: { type: 'contact' | 'user' | 'system'; id?: string; name?: string } | null;
  conversation: {
    id: string;
    status: string;
    channel?: { id: string; channelType: string; channelName: string } | null;
    contact?: {
      id: string;
      firstName?: string;
      lastName?: string;
      phone?: string;
      ProfileURL?: string | null;
    } | null;
  };
}

export interface MessageSearchResult {
  list: MessageSearchHit[];
  total: number;
  page: number;
  totalPages: number;
}

const CHAT_API = {
  // conversation
  filterConversation: 'api/conversations/list',
  conversationStatusCounts: 'api/conversations/status-counts',
  createConversation: 'api/conversations',
  updateConversation: 'api/conversations',
  conversationDetails: 'api/conversations/details',
  deleteConversation: 'api/conversations',
  assignConversation: 'api/conversation/assignconversation',
  // message
  getMessageList: 'api/messages/list',
  searchMessages: 'api/messages/search',
  updateMessageMarkAsRead: 'api/messages/markAsRead',
  updateMessengerMarkAsRead: 'api/messages/markAsRead',
  updateLineMarkAsRead: 'api/message/line-read',
  sendMessage: 'api/messages/send',
  getMessageDetails: 'api/messages',
  messageFavoritesList: 'api/messages/favorites/list',
  // media
  getMediaUrl: 'api/storage/get-signed-url',
  putPresignedMediaUrl: 'api/storage/upload-url',
  // call
  getCallList: 'api/calls/list',
  getCallDetails: 'api/calls/details',
  getCallStreamRecording: 'api/calls/recording',
  exportcall: 'api/calls/export',
  syncCallLoglist: 'api/calls/syncTenantCDR',
  // email
  getEmailList: 'api/messages/list',
  sendEmail: 'api/messages/send',
  // AI
  generateResponse: 'api/gen-ai/generate',
};

export const chatApi = createApi({
  reducerPath: 'chatApi',
  baseQuery,
  endpoints: (builder) => ({
    filterConversation: builder.mutation({
      query: (filterQuery) => ({
        url: CHAT_API.filterConversation,
        method: 'POST',
        body: filterQuery,
        headers: {
          'Content-Type': 'application/json',
        },
      }),
      transformResponse: (response: {
        data?: { list?: any[]; total?: number; totalPages?: number };
      }) => ({
        list: response?.data?.list ?? [],
        totalPages: response?.data?.totalPages ?? 0,
        total: response?.data?.total ?? 0,
      }),
    }),
    // Open / closed counts for the list's filters and search (any status filter is ignored)
    getConversationStatusCounts: builder.mutation<
      ConversationStatusCounts,
      { filters: any[]; globalSearch?: string; combineFieldsGroups?: string[][] }
    >({
      query: (body) => ({
        url: CHAT_API.conversationStatusCounts,
        method: 'POST',
        body,
        headers: {
          'Content-Type': 'application/json',
        },
      }),
      transformResponse: (response: { data?: Partial<ConversationStatusCounts> }) => ({
        open: response?.data?.open ?? 0,
        closed: response?.data?.closed ?? 0,
        total: response?.data?.total ?? 0,
      }),
    }),
    // Message text across all conversations the user can see (min. 2 characters)
    searchMessages: builder.mutation<
      MessageSearchResult,
      { q: string; page: number; limit: number; status?: string; channelId?: string }
    >({
      query: (body) => ({
        url: CHAT_API.searchMessages,
        method: 'POST',
        body,
        headers: {
          'Content-Type': 'application/json',
        },
      }),
      transformResponse: (response: any) => {
        // Accept the list at the top level or wrapped in `data`
        const data = response?.data?.list ? response.data : response;
        return {
          list: data?.list ?? [],
          total: data?.total ?? 0,
          page: data?.page ?? 1,
          totalPages: data?.totalPages ?? 0,
        };
      },
    }),
    createConversation: builder.mutation({
      query: (newConversation) => ({
        url: CHAT_API.createConversation,
        method: 'POST',
        body: newConversation,
        headers: {
          'Content-Type': 'application/json',
        },
      }),
    }),
    updateConversation: builder.mutation({
      query: ({ id, updates }) => ({
        url: `${CHAT_API.updateConversation}/${id}`,
        method: 'PUT',
        body: updates,
        headers: {
          'Content-Type': 'application/json',
        },
      }),
    }),
    patchConversationPin: builder.mutation({
      query: ({
        conversationId,
        pinMessageId,
      }: {
        conversationId: string;
        pinMessageId: string | null;
      }) => ({
        url: `${CHAT_API.updateConversation}/${conversationId}/pin`,
        method: 'PATCH',
        body: { pinMessageId },
        headers: {
          'Content-Type': 'application/json',
        },
      }),
    }),
    toggleMessageFavorite: builder.mutation({
      query: (messageId: string) => ({
        url: `${CHAT_API.getMessageDetails}/${messageId}/favorite`,
        method: 'POST',
        body: {},
        headers: {
          'Content-Type': 'application/json',
        },
      }),
      transformResponse: (response: { data?: { favorited: boolean } }) => ({
        favorited: response?.data?.favorited ?? false,
      }),
    }),
    listMessageFavorites: builder.mutation({
      query: (body: { page: number; limit: number }) => ({
        url: CHAT_API.messageFavoritesList,
        method: 'POST',
        body,
        headers: {
          'Content-Type': 'application/json',
        },
      }),
      transformResponse: (response: {
        data?: {
          favorites?: any[];
          total?: number;
          page?: number;
          limit?: number;
          totalPages?: number;
        };
      }) => ({
        favorites: response?.data?.favorites ?? [],
        total: response?.data?.total ?? 0,
        page: response?.data?.page ?? 1,
        limit: response?.data?.limit ?? 20,
        totalPages: response?.data?.totalPages ?? 0,
      }),
    }),
    listInboxEvents: builder.mutation({
      query: ({
        conversationId,
        ...body
      }: {
        conversationId: string;
        from?: string;
        to?: string;
        q?: string;
        // Only these event types, e.g. ["CONVERSATION_ASSIGNED", "CONVERSATION_UNASSIGNED"]
        eventTypes?: string[];
        page?: number;
        limit?: number;
      }) => ({
        url: `${CHAT_API.updateConversation}/${conversationId}/inbox-events`,
        method: 'POST',
        body,
        headers: {
          'Content-Type': 'application/json',
        },
      }),
      transformResponse: (response: {
        data?: {
          events?: any[];
          total?: number;
          page?: number;
          limit?: number;
          totalPages?: number;
        };
      }) => ({
        events: response?.data?.events ?? [],
        total: response?.data?.total ?? 0,
        page: response?.data?.page ?? 1,
        limit: response?.data?.limit ?? 20,
        totalPages: response?.data?.totalPages ?? 0,
      }),
    }),
    conversationDetails: builder.mutation({
      query: (id: string) => ({
        url: `${CHAT_API.conversationDetails}/${id}`,
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      }),
    }),
    deleteConversation: builder.mutation({
      query: (id: string) => ({
        url: `${CHAT_API.deleteConversation}/${id}`,
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
        },
      }),
    }),
    getMessageList: builder.mutation({
      query: (messageFilter) => ({
        url: CHAT_API.getMessageList,
        method: 'POST',
        body: messageFilter,
        headers: {
          'Content-Type': 'application/json',
        },
      }),
      transformResponse: (response: {
        data?: { messages?: any; totalPages?: number; total?: number };
      }) => ({
        list: response?.data?.messages ?? [],
        totalPages: response?.data?.totalPages ?? 0,
        total: response?.data?.total ?? 0,
      }),
    }),
    updateMessageMarkAsRead: builder.mutation({
      query: (messageFilter) => ({
        url: CHAT_API.updateMessageMarkAsRead,
        method: 'POST',
        body: messageFilter,
        headers: {
          'Content-Type': 'application/json',
        },
      }),
      transformResponse: (response: { data?: { message?: any } }) => ({
        list: response?.data?.message?.list ?? [],
        totalPages: response?.data?.message?.totalPages ?? 0,
      }),
    }),
    updateMessengerMarkAsRead: builder.mutation({
      query: (messageFilter) => ({
        url: CHAT_API.updateMessengerMarkAsRead,
        method: 'POST',
        body: messageFilter,
        headers: {
          'Content-Type': 'application/json',
        },
      }),
    }),
    updateLineMarkAsRead: builder.mutation({
      query: (messageFilter) => ({
        url: CHAT_API.updateLineMarkAsRead,
        method: 'POST',
        body: messageFilter,
        headers: {
          'Content-Type': 'application/json',
        },
      }),
    }),
    sendMessage: builder.mutation({
      query: (messageDetails) => ({
        url: CHAT_API.sendMessage,
        method: 'POST',
        body: messageDetails,
        headers: {
          'Content-Type': 'application/json',
        },
      }),
      transformResponse: (response: { data?: any }) => response?.data ?? null,
    }),
    getMessageDetails: builder.mutation({
      query: (id) => ({
        url: `${CHAT_API.getMessageDetails}/${id}`,
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      }),
    }),
    getMediaUrl: builder.mutation<string, any>({
      query: (fileName) => ({
        url: CHAT_API.getMediaUrl,
        method: 'POST',
        body: fileName,
        headers: {
          'Content-Type': 'application/json',
        },
      }),
      transformResponse: (response: { data?: { url?: string } | string }) => {
        if (typeof response?.data === 'string') {
          return response.data;
        }
        return response?.data?.url ?? '';
      },
    }),
    fetchMediaUrl: builder.query<string, any>({
      query: (fileName) => ({
        url: CHAT_API.getMediaUrl,
        method: 'POST',
        body: fileName,
        headers: {
          'Content-Type': 'application/json',
        },
      }),
      transformResponse: (response: { data?: { url?: string } | string }) => {
        if (typeof response?.data === 'string') {
          return response.data;
        }
        return response?.data?.url ?? '';
      },
    }),
    putPresignedMediaUrl: builder.mutation({
      query: (fileName) => ({
        url: CHAT_API.putPresignedMediaUrl,
        method: 'POST',
        body: fileName,
        headers: {
          'Content-Type': 'application/json',
        },
      }),
      transformResponse: (response: { data?: any }) => response?.data ?? '',
    }),
    getCallList: builder.mutation({
      query: (messageFilter) => ({
        url: CHAT_API.getCallList,
        method: 'POST',
        body: messageFilter,
        headers: {
          'Content-Type': 'application/json',
        },
      }),
      transformResponse: (response: { data?: { call?: any } }) => ({
        list: response?.data?.call?.list ?? [],
        totalPages: response?.data?.call?.totalPages ?? 0,
        total: response?.data?.call?.total,
      }),
    }),
    getCallDetails: builder.mutation({
      query: (id) => ({
        url: `${CHAT_API.getCallDetails}/${id}`,
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      }),
      transformResponse: (response: { data?: any }) => response?.data?.call ?? '',
    }),
    getCallStreamRecording: builder.mutation<string | { error: string }, { id: string }>({
      query: ({ id }) => ({
        url: `${CHAT_API.getCallStreamRecording}/${id}`,
        method: 'GET',
        responseHandler: async (response) => {
          const contentType = response.headers.get('content-type');
          if (contentType?.includes('application/json')) {
            return response.json();
          }
          return response.blob();
        },
      }),
      transformResponse: async (data: Blob | { error: string }) => {
        if ('error' in data) {
          return data;
        }
        const reader = new FileReader();
        return new Promise<string>((resolve, reject) => {
          reader.onloadend = () => resolve(reader.result as string);
          reader.onerror = reject;
          reader.readAsDataURL(data);
        });
      },
    }),
    exportcall: builder.mutation({
      query: (filter) => ({
        url: CHAT_API.exportcall,
        method: 'POST',
        body: filter,
        headers: {
          'Content-Type': 'application/json',
        },
      }),
    }),
    getEmailList: builder.mutation({
      query: (messageFilter) => ({
        url: CHAT_API.getEmailList,
        method: 'POST',
        body: messageFilter,
        headers: {
          'Content-Type': 'application/json',
        },
      }),
      transformResponse: (response: {
        data?: { messages?: any; totalPages?: number; total?: number };
      }) => ({
        list: response?.data?.messages ?? [],
        totalPages: response?.data?.totalPages ?? 0,
        total: response?.data?.total ?? 0,
      }),
    }),
    sendEmail: builder.mutation({
      query: (emailDetails) => ({
        url: CHAT_API.sendEmail,
        method: 'POST',
        body: emailDetails,
        headers: {
          'Content-Type': 'application/json',
        },
      }),
    }),
    syncCallLoglist: builder.mutation({
      query: (emailDetails) => ({
        url: CHAT_API.syncCallLoglist,
        method: 'POST',
        body: emailDetails,
        headers: {
          'Content-Type': 'application/json',
        },
      }),
    }),
    assignConversation: builder.mutation({
      query: (payload) => ({
        url: CHAT_API.assignConversation,
        method: 'POST',
        body: payload,
        headers: {
          'Content-Type': 'application/json',
        },
      }),
    }),

    generateResponse: builder.mutation({
      query: (payload: string) => ({
        url: CHAT_API.generateResponse,
        method: 'POST',
        body: { prompt: payload },
        headers: { 'Content-Type': 'application/json' },
      }),
      transformResponse: (response: { data?: { text: string; usedTokens: number } }) => {
        const generatedText = response.data?.text ?? '';
        return generatedText;
      },
    }),
  }),
});

export const {
  useFilterConversationMutation,
  useGetConversationStatusCountsMutation,
  useSearchMessagesMutation,
  useCreateConversationMutation,
  useConversationDetailsMutation,
  useGetMessageListMutation,
  useSendMessageMutation,
  useGetMediaUrlMutation,
  useFetchMediaUrlQuery,
  usePutPresignedMediaUrlMutation,
  useGetCallListMutation,
  useExportcallMutation,
  useUpdateConversationMutation,
  usePatchConversationPinMutation,
  useToggleMessageFavoriteMutation,
  useListMessageFavoritesMutation,
  useListInboxEventsMutation,
  useGetMessageDetailsMutation,
  useGetCallDetailsMutation,
  useGetCallStreamRecordingMutation,
  useUpdateMessageMarkAsReadMutation,
  useUpdateLineMarkAsReadMutation,
  useUpdateMessengerMarkAsReadMutation,
  useGetEmailListMutation,
  useSendEmailMutation,
  useDeleteConversationMutation,
  useSyncCallLoglistMutation,
  useAssignConversationMutation,

  useGenerateResponseMutation,
} = chatApi;
